/**
 * Zero-commitment demo game (#246, FR-17) — the cold-traffic front door.
 *
 * A logged-out visitor picks 3 curated topics → we create a per-visitor **DYNAMIC +
 * AUTO** demo game (`POST /api/demo/games`, no account), join it with the returned
 * guest identity, and connect the same player WebSocket every real player uses. The
 * curated topics are pre-seeded server-side, so we just auto-send `start_generation`
 * for an instant, zero-LLM board; the in-actor auto-host then reads clues, opens the
 * buzzer, and LLM-judges. The demo is time/clue-boxed server-side (`state.jeopardy.demo`), so it
 * auto-ends to `GAME_OVER` → the convert CTA.
 *
 * This REUSES the shared game surfaces (Board, Buzzer, CluePanel, TimerView,
 * SpokenAnswer, ClueReveal, WagerInput, Scoreboard, GenerationStatus, the clue reader)
 * — it does not reinvent gameplay. It only adds the demo framing: the topic picker, the
 * in-game ribbon (clue/time box), and the end-of-demo convert screen. Power-ups + Final
 * are out of a demo's scope (Classic rules, and the clue box ends it inside round one).
 *
 * Demo-funnel RUM (#148 seam, D246.9): `demo_board_shown` (board materialized),
 * `demo_first_buzz` (first buzz), `demo_convert_clicked` (a convert CTA). The
 * `demo_started` / `demo_completed` conversions are emitted server-authoritatively.
 */

import { gamePhaseOfOrNull } from '@jeopardy/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CURATED_DEMO_TOPICS,
  DEMO_TOPIC_PICK_COUNT,
  gamePhaseOf,
} from '@jeopardy/shared';
import { createDemoGame } from '../net/pack-api';
import { joinAsPlayer } from '../net/game-api';
import { recordFunnelStage } from '../net/rum';
import { useGameConnection } from '../net/use-game';
import { useClueReader } from '../voice/use-clue-reader';
import { LandingHeader } from '../ui/landing/LandingHeader';
import { JoinShare } from '../ui/JoinShare';
import { HIDDEN_PERIL, FINAL_PERIL } from '../ui/brand';
import { Board } from '../ui/Board';
import { Buzzer } from '../ui/Buzzer';
import { CluePanel } from '../ui/CluePanel';
import { ClueReveal } from '../ui/ClueReveal';
import { GenerationStatus } from '../ui/GenerationStatus';
import { Scoreboard } from '../ui/Scoreboard';
import { SpokenAnswer } from '../ui/SpokenAnswer';
import { TimerView } from '../ui/TimerView';
import { WagerInput } from '../ui/WagerInput';
import {
  categoryNameForClue,
  clueReveal,
  currentClue,
  isClueOpen,
  myDailyDoubleWager,
  revealedResponse,
} from '../game/selectors';
import { answerBeingRead, clueBeingRead, isJudging } from '../game/voice-selectors';
import type { GameState } from '@jeopardy/shared';

/** The curated topic picker (pure UI — works offline; only "Start" hits the network). */
function TopicPicker({
  selected,
  onToggle,
  onStart,
  starting,
  error,
}: {
  selected: string[];
  onToggle: (topic: string) => void;
  onStart: () => void;
  starting: boolean;
  error: string | null;
}): React.JSX.Element {
  const full = selected.length >= DEMO_TOPIC_PICK_COUNT;
  return (
    <div className="landing" data-testid="demo-picker-page">
      <LandingHeader />
      <section className="demo-picker">
        <div className="demo-picker__head">
          <span className="landing-eyebrow">Demo game · no account needed</span>
          <h1>Pick {DEMO_TOPIC_PICK_COUNT} topics for your board</h1>
          <p>
            These are ready to play instantly. Your board is built the second you start —
            invite friends with a code, or play solo.
          </p>
        </div>
        <p className="demo-picker__count" data-testid="demo-picker-count">
          Choose <strong>{DEMO_TOPIC_PICK_COUNT}</strong> —{' '}
          <strong>{selected.length}</strong> selected
        </p>
        <ul className="demo-picker__grid" data-testid="demo-picker-grid">
          {CURATED_DEMO_TOPICS.map((topic) => {
            const isSelected = selected.includes(topic);
            return (
              <li key={topic}>
                <button
                  type="button"
                  className={`demo-topic${isSelected ? ' demo-topic--selected' : ''}`}
                  aria-pressed={isSelected}
                  data-testid="demo-topic"
                  // Once 3 are chosen, unselected pills are disabled (pick exactly 3).
                  disabled={!isSelected && full}
                  onClick={() => onToggle(topic)}
                >
                  {topic}
                </button>
              </li>
            );
          })}
        </ul>
        {error && (
          <p className="demo-picker__error" role="alert" data-testid="demo-error">
            {error}
          </p>
        )}
        <div className="demo-picker__actions">
          <button
            type="button"
            className="btn btn--primary"
            data-testid="demo-start"
            disabled={!full || starting}
            onClick={onStart}
          >
            {starting ? 'Starting…' : 'Start the demo →'}
          </button>
        </div>
      </section>
    </div>
  );
}

/** Format ms → "M:SS" (floored at 0) for the ribbon countdown. */
function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Seconds-remaining threshold below which the countdown turns urgent (last-call). */
const DEMO_URGENT_SEC = 15;

/**
 * The in-demo ribbon: DEMO tag, a prominent visible countdown to the box deadline
 * (FR-17.4), and a live convert nudge. The countdown is the demo's bound — it turns
 * urgent in the final {@link DEMO_URGENT_SEC}s so the end never feels abrupt.
 */
function DemoRibbon({ state }: { state: GameState }): React.JSX.Element {
  const endsAt = state.jeopardy.demo?.endsAtEpochMs;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (endsAt === undefined) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  const remainingMs = endsAt !== undefined ? Math.max(0, endsAt - now) : undefined;
  const low = remainingMs !== undefined && remainingMs <= DEMO_URGENT_SEC * 1000;
  return (
    <div className="demo-ribbon" data-testid="demo-ribbon">
      <span className="demo-ribbon__tag">Demo</span>
      {remainingMs !== undefined && (
        <span
          className={`demo-ribbon__timer${low ? ' demo-ribbon__timer--low' : ''}`}
          data-testid="demo-timer"
          aria-label="Time left in the demo"
        >
          {formatCountdown(remainingMs)}
        </span>
      )}
      <span className="demo-ribbon__cta">
        Loving it?{' '}
        <Link to="/host" data-testid="demo-ribbon-host">
          Host your own →
        </Link>
      </span>
    </div>
  );
}

/**
 * What signing up unlocks beyond the demo (FR-17). Game-accurate, brand-correct: the demo
 * is a round-one, auto-hosted taste; hosting your own (free account) unlocks the rest.
 * `HIDDEN_PERIL`/`FINAL_PERIL` are the branded names for Daily Double / Final Jeopardy.
 */
const DEMO_UNLOCKS: { title: string; detail: string }[] = [
  { title: 'Host it yourself', detail: 'be the live host & judge' },
  { title: 'Your own topics', detail: 'any category you want' },
  { title: 'Power-ups', detail: 'Game Night mode' },
  { title: 'House rules', detail: 'family-friendly scoring & more' },
  { title: 'The full board', detail: `${HIDDEN_PERIL} & ${FINAL_PERIL}` },
  { title: 'Saved scores', detail: 'climb the leaderboard' },
];

/** The end-of-demo convert screen (shown on GAME_OVER when it's a demo). */
function DemoConvert({
  score,
  onReplay,
  onConvertClick,
}: {
  score: number;
  onReplay: () => void;
  onConvertClick: () => void;
}): React.JSX.Element {
  return (
    <div className="landing" data-testid="demo-convert-page">
      <LandingHeader />
      <section className="demo-convert">
        <p className="demo-convert__scorelabel">Your demo score</p>
        <div className="demo-convert__score" data-testid="demo-convert-score">
          ${score.toLocaleString('en-US')}
        </div>
        <h1>That’s a taste — the full game has a lot more.</h1>
        <p>
          Every board is generated around the topics your table picks. Sign up free to
          host your own game and unlock everything below.
        </p>
        <ul className="demo-convert__features" data-testid="demo-convert-features">
          {DEMO_UNLOCKS.map((u) => (
            <li key={u.title}>
              {u.title} — {u.detail}
            </li>
          ))}
        </ul>
        <div className="demo-convert__actions">
          <Link
            className="btn btn--primary"
            to="/host"
            data-testid="demo-convert-host"
            onClick={onConvertClick}
          >
            Host your own game →
          </Link>
          <Link
            className="btn btn--ghost"
            to="/host"
            data-testid="demo-convert-signup"
            onClick={onConvertClick}
          >
            Create a free account
          </Link>
        </div>
        <p className="demo-convert__replay">
          <button
            type="button"
            data-testid="demo-convert-replay"
            onClick={onReplay}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Play another demo
          </button>
        </p>
      </section>
    </div>
  );
}

export function DemoPage(): React.JSX.Element {
  const [ticket, setTicket] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  // Retained so the invite QR (JoinShare) can encode this game's join deep link.
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = useCallback((topic: string) => {
    setSelected((prev) => {
      if (prev.includes(topic)) return prev.filter((t) => t !== topic);
      if (prev.length >= DEMO_TOPIC_PICK_COUNT) return prev; // pick exactly 3
      return [...prev, topic];
    });
  }, []);

  const start = useCallback(async () => {
    setStarting(true);
    setError(null);
    try {
      const demo = await createDemoGame(selected);
      // Join the freshly-created demo with the server-minted guest identity, then the
      // same player WS every real player uses carries the game.
      const seat = await joinAsPlayer(
        'You',
        { guestToken: demo.guestToken },
        demo.gameId,
        demo.joinCode,
      );
      setPlayerId(seat.playerId);
      setJoinCode(demo.joinCode);
      setTicket(seat.ticket);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the demo.');
      setStarting(false);
    }
  }, [selected]);

  const replay = useCallback(() => {
    setTicket(null);
    setPlayerId(null);
    setJoinCode(null);
    setSelected([]);
    setStarting(false);
    setError(null);
  }, []);

  const { snapshot, send } = useGameConnection(ticket);
  const state = snapshot.state;

  // Auto-host reads the clue aloud on this client (no-op where TTS is unsupported).
  useClueReader({ state, send });

  // Auto-start generation once the demo lobby is live (topics are pre-seeded server-side,
  // so a demo never shows the category form — it goes straight to a board).
  const startedGen = useRef(false);
  useEffect(() => {
    if (
      state?.jeopardy.demo &&
      gamePhaseOf(state) === 'LOBBY' &&
      state.mode === 'DYNAMIC' &&
      !startedGen.current
    ) {
      startedGen.current = true;
      send({ type: 'start_generation' });
    }
  }, [state?.jeopardy.demo, gamePhaseOfOrNull(state), state?.mode, send]);

  // Funnel seam: the board materialized (first time we reach IN_ROUND).
  const shownBoard = useRef(false);
  useEffect(() => {
    if (gamePhaseOfOrNull(state) === 'IN_ROUND' && !shownBoard.current) {
      shownBoard.current = true;
      recordFunnelStage('demo_board_shown', {
        ...(playerId ? { playerId } : {}),
      });
    }
  }, [gamePhaseOfOrNull(state), playerId]);

  // Funnel seam: the visitor's first buzz.
  const buzzed = useRef(false);
  const onBuzz = useCallback(() => {
    if (!buzzed.current) {
      buzzed.current = true;
      recordFunnelStage('demo_first_buzz', { ...(playerId ? { playerId } : {}) });
    }
    send({ type: 'buzz' });
  }, [playerId, send]);

  const onConvertClick = useCallback(() => {
    recordFunnelStage('demo_convert_clicked', { ...(playerId ? { playerId } : {}) });
  }, [playerId]);

  const myScore = useMemo(
    () => state?.players.find((p) => p.id === playerId)?.score ?? 0,
    [state?.players, playerId],
  );

  // 1) Picker (also the initial screen). Pure UI until "Start the demo".
  if (!ticket) {
    return (
      <TopicPicker
        selected={selected}
        onToggle={toggle}
        onStart={start}
        starting={starting}
        error={error}
      />
    );
  }

  // 2) Convert screen once the boxed demo ends.
  if (gamePhaseOfOrNull(state) === 'GAME_OVER') {
    return (
      <DemoConvert score={myScore} onReplay={replay} onConvertClick={onConvertClick} />
    );
  }

  // 3) In-demo play — the shared game surfaces, framed by the demo ribbon.
  const clue = currentClue(state);
  const clueOpen = isClueOpen(state);
  const clueState = state?.jeopardy.clueState;
  const reveal = clueReveal(state);
  const ddWager = myDailyDoubleWager(state, playerId);
  const buzzerWindowOpen = clueState?.status === 'BUZZER_OPEN';
  const iAmActive = playerId !== null && clueState?.activePlayerId === playerId;
  const iAmLockedOut =
    playerId !== null && (clueState?.lockedOutPlayerIds.includes(playerId) ?? false);
  const canBuzz =
    clueOpen &&
    buzzerWindowOpen &&
    !iAmLockedOut &&
    clueState?.activePlayerId === undefined;
  // Auto-host: the board-control player picks the next clue from their own board.
  const iSelectClues =
    state?.jeopardy.phase === 'IN_ROUND' &&
    playerId !== null &&
    state.jeopardy.boardControlPlayerId === playerId;
  const awardedName =
    reveal?.awardedPlayerId !== undefined
      ? state?.players.find((p) => p.id === reveal.awardedPlayerId)?.name
      : undefined;

  return (
    <div className="app-shell" data-testid="demo-play">
      {state?.jeopardy.demo && <DemoRibbon state={state} />}

      {/* QR-only invite (#246): a friend scans → lands on the guest join deep link and
          plays this exact game. No code/link shown — reuses the JoinShare QR. */}
      {joinCode && (
        <div className="demo-qr" data-testid="demo-invite">
          <JoinShare joinCode={joinCode} variant="qr-only" caption="" />
          <div>
            <p className="demo-qr__title">Play with friends</p>
            <p className="demo-qr__sub">Scan to join this game — no account, no app.</p>
          </div>
        </div>
      )}

      {!snapshot.hasState && <p className="muted">Starting your demo…</p>}

      {state &&
        (gamePhaseOf(state) === 'LOBBY' || gamePhaseOf(state) === 'GENERATING') && (
          <div className="center-col">
            {gamePhaseOf(state) === 'GENERATING' && state.generation ? (
              <GenerationStatus generation={state.generation} />
            ) : (
              <p className="muted" data-testid="demo-building">
                Building your board…
              </p>
            )}
          </div>
        )}

      {state && gamePhaseOf(state) !== 'LOBBY' && gamePhaseOf(state) !== 'GENERATING' && (
        <div className="layout-two-col">
          <div className="center-col">
            {ddWager ? (
              <WagerInput
                bounds={ddWager}
                context={HIDDEN_PERIL}
                onSubmit={(amount) => send({ type: 'submit_wager', amount })}
              />
            ) : reveal && clue ? (
              <ClueReveal
                reveal={reveal}
                response={revealedResponse(state)}
                categoryName={categoryNameForClue(state, clue.id)}
                value={clue.value}
                awardedPlayerName={awardedName}
              />
            ) : clueOpen && clue ? (
              <>
                <CluePanel
                  value={clue.value}
                  prompt={clue.prompt}
                  categoryName={categoryNameForClue(state, clue.id)}
                />
                {state.timer.clueId && <TimerView timer={state.timer} />}
                {clueBeingRead(state) === clue.id && (
                  <p className="muted" data-testid="clue-reading">
                    Reading the clue aloud…
                  </p>
                )}
                {clueState?.mode === 'DAILY_DOUBLE' ? (
                  <p className="muted" data-testid="dd-spectate">
                    {HIDDEN_PERIL} — only the uncovering player answers.
                  </p>
                ) : (
                  <Buzzer
                    canBuzz={canBuzz}
                    active={iAmActive}
                    lockedOut={iAmLockedOut}
                    onBuzz={onBuzz}
                  />
                )}
                <SpokenAnswer state={state} playerId={playerId} send={send} />
                {answerBeingRead(state) === clue.id && (
                  <p className="muted" data-testid="answer-reading">
                    Reading the answer aloud…
                  </p>
                )}
                {isJudging(state) && (
                  <p className="muted" data-testid="judging-indicator">
                    Judging your answer…
                  </p>
                )}
              </>
            ) : (
              <Board
                board={state.jeopardy.board}
                cluesById={state.jeopardy.packSnapshot.cluesById}
                {...(iSelectClues
                  ? {
                      onSelectClue: (clueId: string) =>
                        send({ type: 'select_clue', clueId }),
                    }
                  : {})}
              />
            )}
          </div>
          <div className="stack">
            <Scoreboard
              players={state.players}
              boardControlPlayerId={state.jeopardy.boardControlPlayerId}
              clueState={state.jeopardy.clueState}
              selfPlayerId={playerId ?? undefined}
            />
          </div>
        </div>
      )}

      {/* Feature marquee (#246): advertises what signing up unlocks, below the board on
          the page background. Present throughout the demo play surface. */}
      {state?.jeopardy.demo && (
        <section className="demo-unlock" data-testid="demo-unlock">
          <p className="demo-unlock__head">
            Enjoying the demo?{' '}
            <Link to="/host" data-testid="demo-unlock-signup">
              Sign up free
            </Link>{' '}
            to unlock the full game:
          </p>
          <ul className="demo-unlock__grid">
            {DEMO_UNLOCKS.map((u) => (
              <li key={u.title}>
                <strong>{u.title}</strong> — {u.detail}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
